import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useMemo } from 'react'

import { QueryFetcher, QueryFetcherParams, QueryFetcherResponse } from 'api/common/types'

export function createUseQuery<F extends QueryFetcher & { keepPreviousData?: boolean }, S>({
  queryKey,
  fetcher,
  selector,
  keepPreviousData,
}: {
  queryKey: string[]
  fetcher: F
  selector: (response?: QueryFetcherResponse<F>) => S
  keepPreviousData?: boolean
}) {
  return function useCustomQuery(
    options: { params?: QueryFetcherParams<F> } & Omit<
      UseQueryOptions<QueryFetcherResponse<F>, AxiosError, QueryFetcherResponse<F>, [string, QueryFetcherParams<F>]>,
      'queryKey' | 'queryFn' | 'queryHash' | 'queryKeyHashFn' | 'select' | 'keepPreviousData'
    > = {},
  ) {
    const { params = {} as any, enabled = true, ...queryOptions } = options

    const { data, isLoading, ...rest } = useQuery({
      queryKey: [queryKey.join('-'), params],
      ...{ enabled, ...queryOptions },
      queryFn: ({ signal }: { signal: any }) => fetcher(params)(signal) as QueryFetcherResponse<F>,
      ...(keepPreviousData ? { placeholderData: (prev: any) => prev } : {}),
    })

    return {
      ...rest,
      isLoading: enabled && isLoading,
      data: useMemo(() => selector(data), [data]),
      response: data,
    }
  }
}
